import { useLocation } from "react-router";
import { ApplicationType } from "@domain/application/application";
import { CountryCode } from "@domain/currentProfile/countryCode";
import { GroupType } from "@domain/group/group";
import { ContentLicense } from "@domain/licence/contentLicense";
import { LivecourseType } from "@domain/livecourse/livecourse";
import { Category, categorySlug, SubCategory } from "@domain/lms/category/category";
import { ContentLanguage } from "@domain/lms/contentLanguage";
import { ContentLevel } from "@domain/lms/contentLevel";
import { CourseDuration, CourseProvider } from "@domain/lms/course/course";
import { OrganizationSubType, OrganizationType } from "@domain/organization/organizationType";
import { Scoring } from "@domain/ranking/ranking";
import { SearchDomain } from "@domain/search/searchDomain";

export const profileTabs = ["activity", "content", "shareroom", "favorite"] as const;
export type ProfileTab = (typeof profileTabs)[number];

// export const instituteTabs = ["presentation", "activity", "tracks", "content", "community", "shareroom"] as const;
export const instituteTabs = ["activity", "content", "presentation", "shareroom", "tracks", "community"] as const;
export type InstituteTab = (typeof instituteTabs)[number];
// export const companyTabs = ["presentation", "activity", "content", "community", "shareroom"] as const;
export const companyTabs = ["activity", "content", "presentation", "shareroom", "community"] as const;
export type CompanyTab = (typeof companyTabs)[number];

export const groupTabs = [
	"about",
	"dashboard",
	"workspaces",
	"community",
	"live",
	"activity",
	"feedback",
	"pdf",
] as const;
export type GroupTab = (typeof groupTabs)[number];

export const discussionTabs = ["activity", "community"] as const;
export type DiscussionTab = (typeof discussionTabs)[number];

export const shareroomTabs = groupTabs;
export type ShareroomTab = (typeof shareroomTabs)[number];

type IdOrSlug = {
	id?: string;
	slug?: string;
};

export const Path = {
	Root: "/",
	Test: "/test",
	// ArticleEditor: (id: string) => `/article/edit/${id}`,
	ArticleEditor: (id: string, workspace?: string) => `/article/edit/${id}${workspace ? `?workspace=${workspace}` : ""}`,
	Article: (id: string) => `/articles/${id}`,
	ReferenceArticle: ({ id, slug, category }: { id?: string; slug?: string; category?: Category | ":category" }) => {
		if (!slug) {
			return `/articles/${id}`;
		}
		const categoryPath = category ? (category === ":category" ? ":category?" : `${categorySlug(category)}`) : "";

		return `/articles/${categoryPath}/${slug}`;
	},
	Carousel: (id: string) => `/carousels/${id}`,
	ReferenceCarousel: ({ id, slug, category }: { id?: string; slug?: string; category?: Category | ":category" }) => {
		if (!slug) {
			return `/carousels/${id}`;
		}
		const categoryPath = category ? (category === ":category" ? ":category?" : `${categorySlug(category)}`) : "";

		return `/carousels/${categoryPath}/${slug}`;
	},
	Profile: ({ id, slug }: IdOrSlug, tab?: ProfileTab | ":tab?", scrollToTab = false) =>
		`/profile/${slug ?? `@${id}`}/${tab ?? ""}${scrollToTab ? "#profile-tab" : ""}`,
	MyProfile: "/profile",
	Lms: {
		Course: (id: string) => `/lms/courses/${id}`,
		ReferenceCourse: ({
			id,
			language,
			slug,
			category,
			subCategory,
		}: {
			id?: string;
			language: ContentLanguage | ":language";
			slug?: string;
			category?: Category | ":category";
			subCategory?: SubCategory | ":subCategory";
		}) => {
			if (!slug) {
				return `/lms/courses/${id}`;
			}
			const categoryPath = category ? (category === ":category" ? "/:category?" : `/${categorySlug(category)}`) : "";
			const subCategoryPath = subCategory
				? subCategory === ":subCategory"
					? "/:subCategory?"
					: `/${categorySlug(subCategory)}`
				: "";

			return `/courses/${language.toLowerCase()}${categoryPath}${subCategoryPath}/${slug}`;
		},
		CourseContent: (id: string, chapter?: number, section?: string) =>
			`/lms/courses/${id}/content${chapter !== undefined ? `?chapter=${chapter}${section ? `#${section}` : ""}` : ""}`,
		Editor: (id: string, workspace?: string) => `/lms/edit/${id}${workspace ? `?workspace=${workspace}` : ""}`,
	},
	Quiz: (id: string, mobile = true, participantId?: string): string => {
		if (participantId) {
			return mobile ? `/quiz/${id}/${participantId}` : `/?quiz=${id}&source=${participantId}`;
		}

		return mobile ? `/quiz/${id}` : `/?quiz=${id}`;
	},
	QuizEditor: (id: string, workspace?: string) => `/quiz/edit/${id}${workspace ? `?workspace=${workspace}` : ""}`,
	Organization: ({ id, slug }: IdOrSlug, tab?: InstituteTab | CompanyTab | ":tab?", scrollToTab = false) =>
		`/organization/${slug ?? `@${id}`}/${tab ?? ""}${scrollToTab ? "#org-tab" : ""}`,
	Discussion: ({ id, slug }: IdOrSlug, tab?: DiscussionTab | ":tab?") => `/group/${slug ?? `@${id}`}/${tab ?? ""}`,
	Shareroom: ({ id, slug }: IdOrSlug, tab?: ShareroomTab | ":tab?", workspaceId?: string) =>
		`/shareroom/${slug ?? `@${id}`}/${tab ?? (workspaceId ? "workspaces" : "")}${workspaceId ? `/${workspaceId}` : ""}`,

	GroupEditor: (id: string, type: GroupType, organizationId?: string) =>
		`/groups/edit/${type}/${id}${organizationId ? `?organizationId=${organizationId}` : ""}`,
	ApplicationEditor: (id: string, type: ApplicationType) => `/program/${type}/apply/edit/${id}`,
	ApplicationLP: "/program/omat",
	Messaging: "/app/messaging",
	Chatmessage: (profileId: string, channel?: string) =>
		`/Chatmessage/${profileId}?${channel ? `channel=${channel}` : ""}`,
	ApplicationSheet: ({ id, slug }: IdOrSlug) => `/program/omat/view/application/${slug ?? `@${id}`}`,
	LiveGroupEditor: (shareroomId: string, type: LivecourseType, liveId: string) =>
		`/livecourses/Group/edit/${type}/${shareroomId}/${liveId}`,
	LiveOrganizationEditor: (organizationId: string, type: LivecourseType, liveId: string) =>
		`/livecourses/Organization/edit/${type}/${organizationId}/${liveId}`,
	LiveGroupInfos: (livecourseId: string, shareroomId: string) => `/livecourses/Group/${livecourseId}/${shareroomId}`,
	LiveOrganizationInfos: (livecourseId: string, organizationId: string) =>
		`/livecourses/Organization/${livecourseId}/${organizationId}`,
	NewOrganization: "/organization/new",
	Search: "/search",
	Post: (id: string) => `/post/${id}`,
	FirebaseAction: "/firebaseAction",
	LinkNumberAction: "/linkNumberAction",
	ChangeEmailAction: "/changeEmailAction",
	More: "/more",
	Cgu: "/cgu",
	Debug: "/debug",
	Settings: "/settings",
	AssetManager: "/assetManager",
	Backoffice: "/backoffice",
} as const;

export function searchParams(
	query?: string,
	domain?: SearchDomain,
	categories?: Category[],
	levels?: ContentLevel[],
	languages?: ContentLanguage[],
	durations?: CourseDuration[],
	providers?: CourseProvider[],
	orgTypes?: OrganizationType[],
	orgSubTypes?: OrganizationSubType[],
	countries?: CountryCode[],
	license?: ContentLicense[],
	scoring?: Scoring[],
	rating?: number
): string {
	const qParam = query !== undefined ? `q=${query}` : "";
	const domainParam = domain !== undefined ? `domain=${domain}` : "";
	const categoriesParam = categories !== undefined ? `categories=${categories.join(",")}` : "";
	const levelsParam = levels !== undefined ? `levels=${levels.join(",")}` : "";
	const languagesParam = languages !== undefined ? `languages=${languages.join(",")}` : "";
	const durationsParam = durations !== undefined ? `durations=${durations.join(",")}` : "";
	const providersParam = providers !== undefined ? `providers=${providers.join(",")}` : "";
	const orgTypesParam = orgTypes !== undefined ? `type=${orgTypes.join(",")}` : "";
	const orgSubTypesParam = orgSubTypes !== undefined ? `subTypes=${orgSubTypes.join(",")}` : "";
	const countriesParam = countries !== undefined ? `countries=${countries.join(",")}` : "";
	const licenseParam = license !== undefined ? `license=${license}` : "";
	const scoringParam = scoring !== undefined ? `meanFeedbackScore=${scoring}` : "";
	const ratingParam = rating !== undefined ? `averageFeedback=${rating}` : "";
	return `?${[
		qParam,
		domainParam,
		orgTypesParam,
		orgSubTypesParam,
		categoriesParam,
		levelsParam,
		languagesParam,
		durationsParam,
		providersParam,
		countriesParam,
		licenseParam,
		scoringParam,
		ratingParam,
	]
		.filter((p) => !!p)
		.join("&")}`;
}

export function useMobileTabBarVisible(): boolean {
	const location = useLocation();
	const paths = [Path.Lms.Editor(""), Path.QuizEditor(""), Path.Post(""), Path.Search, Path.AssetManager];

	if (new RegExp(Path.Lms.CourseContent("[\\w*\\d*]*")).exec(location.pathname)) {
		return false;
	}
	if (paths.find((path) => location.pathname.startsWith(path))) {
		return false;
	}
	return true;
}

export function linkDescriptor(path: string, state?: unknown): { pathname: string; search?: string; state?: unknown } {
	const [pathname, search] = path.split("?");
	return { pathname, search: search ? `?${search}` : undefined, state };
}
